import React, { FC, useMemo } from 'react';
import { StepCircleStatus } from 'modules/core/enums/stepCircleStatus.enum';
import { COLORS } from 'modules/core/theme/colors';

import { BaseCircle, CheckmarkPath, SpinnerCircle, StepCircleWrapper } from './styles';

interface IStepCircleProps {
  status: StepCircleStatus;
  size?: number;
  stroke?: number;
}

export const StepCircle: FC<IStepCircleProps> = ({ status, size = 16, stroke = 2 }) => {
  const center = size / 2;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;

  const overlayStroke = status === StepCircleStatus.FAILED ? COLORS.RED_7 : COLORS.GREEN_16;

  const baseCircleFill = useMemo(() => {
    if (status === StepCircleStatus.IN_PROGRESS) return 'transparent';

    if (status === StepCircleStatus.COMPLETED) return COLORS.GREEN_16;

    if (status === StepCircleStatus.FAILED) return COLORS.RED_7;

    return COLORS.GREY_43;
  }, [status]);

  const baseCircleStroke = useMemo(() => {
    if (status === StepCircleStatus.COMPLETED) return COLORS.GREEN_16;

    if (status === StepCircleStatus.FAILED) return COLORS.RED_7;

    return COLORS.GREY_43;
  }, [status]);

  return (
    <StepCircleWrapper $size={size}>
      <svg width={size} height={size} role="img">
        <BaseCircle
          cx={center}
          cy={center}
          r={radius}
          fill={baseCircleFill}
          stroke={baseCircleStroke}
          strokeWidth={stroke}
        />

        {status === StepCircleStatus.IN_PROGRESS && (
          <SpinnerCircle
            cx={center}
            cy={center}
            r={radius}
            fill="transparent"
            stroke={overlayStroke}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${circumference * 0.65} ${circumference * 0.35}`}
          />
        )}

        {status === StepCircleStatus.FAILED ? (
          <path
            d={`
              M ${size * 0.35} ${size * 0.35}
              L ${size * 0.65} ${size * 0.65}
              M ${size * 0.65} ${size * 0.35}
              L ${size * 0.35} ${size * 0.65}
            `}
            fill="none"
            stroke={COLORS.WHITE}
            strokeWidth={stroke}
            strokeLinecap="round"
          />
        ) : (
          <CheckmarkPath
            d={`
              M ${size * 0.28} ${size * 0.52}
              L ${size * 0.43} ${size * 0.67}
              L ${size * 0.72} ${size * 0.36}
            `}
            fill="none"
            stroke={status === StepCircleStatus.IN_PROGRESS ? COLORS.GREY_43 : COLORS.WHITE}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
      </svg>
    </StepCircleWrapper>
  );
};
